import React from 'react'
import { WifiOff, RefreshCw, CheckCircle } from 'lucide-react'
import { clsx } from 'clsx'

interface OfflineBannerProps {
  isOnline: boolean
  pendingCount: number
  isSyncing: boolean
  lastSyncAt: Date | null
  onManualSync: () => void
}

export default function OfflineBanner({
  isOnline, pendingCount, isSyncing, lastSyncAt, onManualSync,
}: OfflineBannerProps) {
  // Nothing to show if online and no pending
  if (isOnline && pendingCount === 0) return null

  if (!isOnline) {
    return (
      <div className="fixed top-0 left-0 right-0 z-50 bg-slate-800 text-white px-4 py-2.5 flex items-center gap-2.5 safe-top">
        <WifiOff className="w-4 h-4 flex-shrink-0 text-amber-400" />
        <div className="flex-1 min-w-0">
          <p className="text-xs font-semibold">You're offline</p>
          {pendingCount > 0 && (
            <p className="text-[10px] text-white/60">
              {pendingCount} record{pendingCount > 1 ? 's' : ''} will sync when you reconnect
            </p>
          )}
        </div>
      </div>
    )
  }

  // Online but has pending writes
  if (pendingCount > 0) {
    return (
      <div className="fixed top-0 left-0 right-0 z-50 bg-amber-500 text-white px-4 py-2.5 flex items-center gap-2.5 safe-top">
        {isSyncing ? (
          <RefreshCw className="w-4 h-4 flex-shrink-0 animate-spin" />
        ) : (
          <RefreshCw className="w-4 h-4 flex-shrink-0" />
        )}
        <div className="flex-1 min-w-0">
          <p className="text-xs font-semibold">
            {isSyncing ? 'Syncing...' : `${pendingCount} record${pendingCount > 1 ? 's' : ''} pending sync`}
          </p>
        </div>
        {!isSyncing && (
          <button
            onClick={onManualSync}
            className="text-[11px] font-bold bg-white/20 px-2.5 py-1 rounded-lg hover:bg-white/30 transition-colors flex-shrink-0"
          >
            Sync Now
          </button>
        )}
      </div>
    )
  }

  return null
}

// ─── Sync status indicator for forms ─────────────────────────
export function SyncStatusDot({ isOnline }: { isOnline: boolean }) {
  return (
    <div className="flex items-center gap-1.5">
      <div className={clsx(
        'w-2 h-2 rounded-full flex-shrink-0',
        isOnline ? 'bg-emerald-500' : 'bg-amber-500'
      )} />
      <span className="text-[10px] text-text-tertiary font-medium">
        {isOnline ? 'Online' : 'Offline — will sync'}
      </span>
    </div>
  )
}
